import { selectedMainObligationAtom } from "@/atoms/obligations"
import { LiquidationProgress } from "@/components/liquidationProgress"
import useIsMobile from "@/hooks/useMobileDevice"
import useTrans from "@/hooks/useTrans"
import { SKELETON_VALUE } from "@/utils/constants"
import { replaceLocaleStringWithValues } from "@/utils/helpers"
import { formatUsd } from "@/utils/numberFormatter"
import { Stack, Typography } from "@mui/material"
import BigNumber from "bignumber.js"
import "dayjs/locale/vi"
import { useAtom } from "jotai"
import { useMemo, useState } from "react"

const formatPercentUtilBar = (value: BigNumber) =>
  value.multipliedBy(100).toNumber().toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })

const BorrowLimits = () => {
  const trans = useTrans()
  const [progressWidth, setProgressWidth] = useState(0)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const isMobileDevice = useIsMobile()

  const usedObligation = useMemo(
    () =>
      selectedObligation ?? {
        totalSupplyValue: new BigNumber(0),
        totalBorrowValue: new BigNumber(0),
        borrowLimit: new BigNumber(0),
        liquidationThreshold: new BigNumber(0),
        borrowOverSupply: new BigNumber(0),
        borrowLimitOverSupply: new BigNumber(0),
        liquidationThresholdFactor: new BigNumber(0),
        weightedTotalBorrowValue: new BigNumber(0),
        weightedBorrowUtilization: new BigNumber(0),
      },
    [selectedObligation],
  )

  const borrowLimitOverSupply = useMemo(
    () =>
      usedObligation.totalSupplyValue.isZero()
        ? new BigNumber(0)
        : usedObligation.borrowLimit.dividedBy(usedObligation.totalSupplyValue),
    [usedObligation],
  )

  const weightedBorrowOverSupply = useMemo(
    () =>
      usedObligation.totalSupplyValue.isZero()
        ? new BigNumber(0)
        : usedObligation.weightedTotalBorrowValue.dividedBy(usedObligation.totalSupplyValue),
    [usedObligation],
  )

  const liquidationThresholdOverSupply = useMemo(
    () =>
      usedObligation.totalSupplyValue.isZero()
        ? new BigNumber(0)
        : usedObligation.liquidationThreshold.dividedBy(usedObligation.totalSupplyValue),
    [usedObligation],
  )

  const currentProgressTitle = useMemo(
    () =>
      replaceLocaleStringWithValues(
        trans.dashboard.hero.current_progress_title,
        formatPercentUtilBar(weightedBorrowOverSupply),
        formatPercentUtilBar(weightedBorrowOverSupply.dividedBy(borrowLimitOverSupply)),
      ),
    [borrowLimitOverSupply, weightedBorrowOverSupply, trans],
  )

  const borrowLimitTitle = useMemo(
    () =>
      replaceLocaleStringWithValues(
        trans.dashboard.hero.borrow_limit_title,
        formatUsd(borrowLimitOverSupply.multipliedBy(usedObligation.totalSupplyValue)),
        formatPercentUtilBar(borrowLimitOverSupply),
      ),
    [borrowLimitOverSupply, usedObligation.totalSupplyValue, trans],
  )

  const liquidationThresholdTitle = useMemo(
    () =>
      replaceLocaleStringWithValues(
        trans.dashboard.hero.liquidation_threshold_title,
        formatUsd(usedObligation.liquidationThreshold),
        formatPercentUtilBar(liquidationThresholdOverSupply),
      ),
    [liquidationThresholdOverSupply, usedObligation.liquidationThreshold, trans],
  )

  const supplyTitle = useMemo(
    () => replaceLocaleStringWithValues(trans.dashboard.hero.supply_title, formatUsd(usedObligation.totalSupplyValue)),
    [usedObligation.totalSupplyValue, trans],
  )

  const borrowLimitDisplay = useMemo(() => {
    return !selectedObligation
      ? SKELETON_VALUE
      : usedObligation.borrowLimit.isGreaterThan(0)
        ? formatUsd(usedObligation.borrowLimit)
        : "0"
  }, [selectedObligation, usedObligation.borrowLimit])

  const liquidationThresholdDisplay = useMemo(() => {
    if (!selectedObligation) return SKELETON_VALUE
    if (usedObligation.liquidationThreshold.isLessThanOrEqualTo(0)) return "0"

    return formatUsd(usedObligation.liquidationThreshold)
  }, [selectedObligation, usedObligation.liquidationThreshold])

  if (
    isMobileDevice &&
    !selectedObligation &&
    usedObligation.liquidationThreshold.isLessThanOrEqualTo(0) &&
    usedObligation.borrowLimit.isLessThanOrEqualTo(0)
  ) {
    return <></>
  }

  return (
    <Stack data-testid='my-account-borrow-limits' className='bg-white py-4 px-4 sm:px-5 rounded-lg h-full' gap={2}>
      <Stack>
        <Typography className='font-bold text-xl'>{trans.dashboard.hero.borrow_limits}</Typography>
        <Typography className='text-x-xs text-neutral-600'>{trans.dashboard.hero.subtitle_borrow_limits}</Typography>
      </Stack>
      <Stack className='bg-gradient-box-status rounded-lg py-6 px-5 h-full' justifyContent='space-between' gap={1.5}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Stack>
            <Typography className='text-neutral-600 text-xs sm:text-sm'>
              {trans.dashboard.hero.user_borrow_limit}
            </Typography>
            <Typography className='text-sm sm:text-xl font-bold text-orange-600'>{borrowLimitDisplay}</Typography>
          </Stack>
          <Stack>
            <Typography className='text-neutral-600 text-xs sm:text-sm'>
              {trans.dashboard.hero.liquidation_threshold}
            </Typography>
            <Typography className='text-sm sm:text-xl font-bold text-red-600 text-right'>
              {liquidationThresholdDisplay}
            </Typography>
          </Stack>
        </Stack>
        <LiquidationProgress
          currentProgress={weightedBorrowOverSupply.toNumber()}
          progressWidth={progressWidth}
          borrowLimit={borrowLimitOverSupply.toNumber()}
          liquidationThreshold={liquidationThresholdOverSupply.toNumber()}
          setProgressWidth={setProgressWidth}
          currentProgressTitle={currentProgressTitle}
          borrowLimitTitle={borrowLimitTitle}
          liquidationThresholdTitle={liquidationThresholdTitle}
          supplyTitle={supplyTitle}
        />
      </Stack>
    </Stack>
  )
}

export default BorrowLimits
